import { EMAIL_CENTER_ACTIONS } from '@/constants/actions';
import {
  discardEmailTemplateDraftRequest,
  publishEmailTemplateDraftRequest,
} from '@/services/api/emailCenter';
import { INTL_ENTITY } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import { useCallback, useState } from 'react';
import { useDispatch } from 'umi';

interface Options {
  /** The template being edited; `null` while creating, where no draft exists. */
  templateId?: string | null;
  /**
   * Closes the drawer once the draft is gone, published or discarded. The
   * *unguarded* close: the action has already decided what happens to the work
   * on screen, so an "unsaved changes?" prompt on top of it would be asking
   * about a choice the user just made.
   */
  close: () => void;
}

/**
 * The outstanding draft: publish it, discard it, and the two flags that keep
 * the drawer honest about it.
 *
 * `draftBusy` is the action in flight. `draftLoading` is the *fetch* of the
 * draft on open, owned here but set by the drawer's fill: saving has to be held
 * until it lands, or a fast click writes the published values that are on
 * screen for that instant, silently overwriting the draft it was about to
 * replace them with.
 *
 * Publishing and discarding both refetch the list and close, because either way
 * what is on screen is stale — publishing replaced those values, discarding
 * confirmed them.
 */
export function useTemplateDraftActions({ templateId, close }: Options) {
  const dispatch = useDispatch();
  const [draftBusy, setDraftBusy] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);

  const runDraftAction = useCallback(
    async (action: 'publish' | 'discard') => {
      if (!templateId) return;
      setDraftBusy(true);
      const result =
        action === 'publish'
          ? await publishEmailTemplateDraftRequest({
              params: { uuid: templateId },
            })
          : await discardEmailTemplateDraftRequest({
              params: { uuid: templateId },
            });
      setDraftBusy(false);

      processAPIResponseNow(result, {
        showSuccessMessage: true,
        successEntity: INTL_ENTITY.EMAIL_TEMPLATE,
        onSuccess: () => {
          // Clear the flag before closing: the URL can reopen this drawer from the
          // pool faster than the refetch lands, and a banner that outlives its
          // draft is worse than one that arrives late.
          dispatch({
            type: EMAIL_CENTER_ACTIONS.PATCH_DRAWER_RECORD,
            payload: { drawer: 'template', record: { has_draft: false } },
          });
          close();
          dispatch({ type: EMAIL_CENTER_ACTIONS.FETCH_TEMPLATES, payload: {} });
        },
      });
    },
    [templateId, close, dispatch],
  );

  return { draftBusy, draftLoading, setDraftLoading, runDraftAction };
}

export default useTemplateDraftActions;
